import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { PasswordInput } from "@/components/PasswordInput";
import { Waves, Loader2, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) return setErr("Password must be at least 8 characters");
    if (pw !== confirm) return setErr("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2200);
    } catch (e2) {
      setErr(apiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--bg-app)" }}>
      <div className="w-full max-w-sm sg-card p-6 sm:p-8 sg-fade" data-testid="reset-form">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--accent)" }}>
            <Waves size={22} color="#fff" />
          </div>
          <div className="font-head text-xl font-extrabold">SHADOW GUIDE</div>
        </div>

        {!token ? (
          <div data-testid="reset-missing-token">
            <h2 className="font-head text-2xl font-bold mb-2">Invalid link</h2>
            <p className="text-sm mb-6" style={{ color: "var(--txt2)" }}>This reset link is missing its token. Please request a new one.</p>
            <Link to="/forgot-password" className="sg-btn sg-btn-accent w-full justify-center" data-testid="reset-request-new">Request a new link</Link>
          </div>
        ) : done ? (
          <div data-testid="reset-success">
            <CheckCircle2 size={34} style={{ color: "var(--accent)" }} className="mb-3" />
            <h2 className="font-head text-2xl font-bold mb-2">Password updated</h2>
            <p className="text-sm mb-6" style={{ color: "var(--txt2)" }}>Redirecting you to sign in…</p>
            <Link to="/login" className="sg-btn w-full justify-center" data-testid="reset-go-login">Go to sign in</Link>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h2 className="font-head text-2xl font-bold mb-1">Set a new password</h2>
            <p className="text-sm mb-6" style={{ color: "var(--txt2)" }}>Choose a strong password (min 8 characters).</p>

            {err && <div className="chip mb-4 w-full justify-center" data-testid="reset-error"
              style={{ background: "rgba(239,68,68,0.15)", color: "#EF4444", borderColor: "#EF4444" }}>{err}</div>}

            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>New password</label>
            <PasswordInput className="mt-1 mb-4" value={pw} data-testid="reset-password"
              onChange={(e) => setPw(e.target.value)} autoComplete="new-password" required />

            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Confirm password</label>
            <PasswordInput className="mt-1 mb-6" value={confirm} data-testid="reset-confirm"
              onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" required />

            <button className="sg-btn sg-btn-accent w-full" disabled={busy} data-testid="reset-submit">
              {busy ? <Loader2 className="animate-spin" size={18} /> : "Update password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
